/**
 *  PremiumCompute: class with static methods
 *
 *  init(schemes, isCust = false): ** MUST BE CALLED ** for initialization
 *      schemes: object[] => premium schemes list
 *      isCust: boolean => whether is initialized for customer or producer (if customer, then 'true')
 *  return <= void
 *
 *  compute(schemeTypeName, selectedPackageNameList): returns computed premium data object.
 *      schemeTypeName: string => scheme type for which the premium data needs to be calculated.
 *      selectedPackageNameList: string[] => selected package name list.
 *  return <= COMPUTE_PREMIUM_DATA_OBJECT
 *
 *  getAsIs(): return the As Is (current plan) premium data object (pre-computed).
 *  return <= COMPUTE_PREMIUM_DATA_OBJECT
 *
 *  getRecommended(): return the recommended plan premium data object (pre-computed).
 *  return <= COMPUTE_PREMIUM_DATA_OBJECT
 *          May also be null, in possible cases.
 *
 *  prepareInitMapping(): return the initialization info for all scheme type (to be used in customization)
 *  return <= Object
 *          {
 *              tenureList: TENURE_LIST,
 *              siList: SI_LIST,
 *              tenureSiSchemeMap: TENURE_SI_MAP,
 *          }
 *      TENURE_LIST: Object []                              // May be empty on the basis of edge case condition handled in function.
 *              [{
 *                  id: string;                             // Format: _T-{{tenure}}
 *                  value: number;
 *                  tenure: number;
 *                  disabled: boolean;
 *              }]
 *      SI_LIST: Object []                                  // May be empty on the basis of edge case condition handled in function.
 *              [{
 *                  id: string;                             // Format: _SI-{{shortened_si}}
 *                  value: number;                          // shortened SI
 *                  si: number;                             // complete SI
 *                  disabled: boolean;
 *              }]
 *      TENURE_SI_MAP: Object
 *              {
 *                  [key: string]: {                        // key: format (tenure.id + si.id) from above list
 *                      schemeTypeNameTn: string,           // Transformed name (spaces removed and to lower case)
 *                      schemeTypeName: string,             // pristine scheme name
 *                      packagesSelected: string[],         // Only keys list available in package Object
 *                      packages: Object                    // keys with package data as backend response
 *                  }
 *              }
 *
 *  describeAll(allPackageSelected = false): return the recommended plan premium data object (real-time-computation)
 *      allPackageSelected: boolean => If true, will by default include package value in COMPUTE_PREMIUM_DATA_OBJECT
 *  return <= Object
 *          {
 *              [key: string]: COMPUTE_PREMIUM_DATA_OBJECT
 *          }
 *
 *  COMPUTE_PREMIUM_DATA_OBJECT:
 *  {
 *      schemeTypeNameTn: string,
 *      schemeTypeName: string,
 *      basePremium: number,
 *      totalDiscount: number,
 *      totalLoading: number,
 *      premiumWithoutGST: number,
 *      premiumGST: number,
 *      premiumGSTVal: number,
 *      premiumInDecimal: number,
 *      premium: number,
 *      packageCount: number,
 *      packageList: string[],                                  // Package Object keys selected for this scheme.
 *      riders: string[],
 *      packagePremiumWithGST: number,
 *      packagePremiumBreakup: {
 *          premiumInDecimal: number,
 *          premium: number,
 *          gstInDecimal: number,
 *          gst: number,
 *      }
 *      totalPremium: number,
 *      totalPremiumInDecimal: number,
 *      tenureSI: {
 *          tenureId: string,                                   // Format: _T-{{tenure}}
 *          tenure: number,
 *          sumInsuredId: string,                               // Format: _SI-{{shortened_si}}
 *          sumInsured: number,
 *          shortSumInsured: number,                            // Shortened SI
 *      }
 *      benefits: {
 *          savePercentage: number,
 *          additionalSI: number
 *      }
 *  }
 */
class PremiumCompute {
  static #instance;

  /**
   *  Object with:
   *      key: as schemeTypeName (space removed and to lower case)
   *      value: complete scheme data received received at the time time of init.
   */
  #schemeTypeMap = {};

  /**
   *  Object with:
   *      key: as schemeTypeName (space removed and to lower case)
   *      value: {
   *          sumInsured: number,
   *          tenure: number,
   *      }
   */
  #schemeTypeInfoMap = {};

  /**
   *  Object with precomputed asis data
   */
  #asisPremiumData = null;

  /**
   *  Object with precomputed recommended data
   */
  #recommendedPremiumData = null;

  /**
   *  Object with precomputed exception data
   */
  #exceptionAsisPremiumData = null;

  /**
   *  boolean to store initialize type as customer
   */
  #isCust = false;

  #ASIS_KEY = "asis";
  #RECOMMENDED_KEY = "highersislab1";
  #EXCEPTION_KEY = "exceptionas-is";
  #SI_MULTIPLE_IN = 100000;
  allSchemes = {};

  constructor(schemes, isCust = false) {
    if (!Array.isArray(schemes)) {
      throw new Error("Something went wrong");
    }
    this.#isCust = isCust;
    let recommendedScheme = null;
    schemes.forEach((scheme) => {
      const schemeTypeNameTn = this.#getSchemeTypeTN(scheme.schemeType);
      this.#schemeTypeMap[schemeTypeNameTn] = scheme;
      this.#schemeTypeInfoMap[schemeTypeNameTn] = {
        sumInsured: this.#getSI(scheme),
        tenure: scheme?.tenure || 0,
      };
      if (isCust) {
        if (schemeTypeNameTn === this.#ASIS_KEY) {
          this.#asisPremiumData = this.#computePremium(
            schemeTypeNameTn,
            Object.entries(scheme?.packages || {})
              .filter(([_, pkg]) => pkg?.isMandatory && pkg?.value !== 0)
              .map(([key]) => key)
          );
        }
        if (schemeTypeNameTn === this.#RECOMMENDED_KEY) {
          recommendedScheme = scheme;
        }
      } else {
        if (schemeTypeNameTn === this.#ASIS_KEY) {
          this.#asisPremiumData = this.#computePremium(
            schemeTypeNameTn,
            Object.entries(scheme?.packages || {})
              .filter(([_, pkg]) => pkg?.isMandatory && pkg?.value !== 0)
              .map(([key]) => key)
          );
        }
        if (schemeTypeNameTn === this.#RECOMMENDED_KEY) {
          this.#recommendedPremiumData = this.#computePremium(
            schemeTypeNameTn,
            Object.entries(scheme?.packages || {})
              .filter(([_, pkg]) => pkg?.isMandatory && pkg?.value !== 0)
              .map(([key]) => key)
          );
        }
        if (schemeTypeNameTn === this.#EXCEPTION_KEY) {
          const exceptionKey = this.#getSchemeTypeTN(scheme.schemeType);
          this.#exceptionAsisPremiumData = this.#computePremium(
            exceptionKey,
            Object.entries(scheme?.packages || {})
              .filter(([_, pkg]) => pkg?.isMandatory && pkg?.value !== 0)
              .map(([key]) => key)
          );
        }
      }
    });

    if (isCust) {
      if (recommendedScheme) {
        this.#recommendedPremiumData = this.#computePremium(
          this.#RECOMMENDED_KEY,
          Object.entries(recommendedScheme?.packages || {})
            .filter(([_, pkg]) => pkg?.isMandatory && pkg?.value !== 0)
            .map(([key]) => key)
        );
      } else {
        const asisTenure = this.#schemeTypeInfoMap[this.#ASIS_KEY]?.tenure || 0;
        schemes.forEach((scheme) => {
          const tenure = scheme?.tenure || 0;
          if (tenure - asisTenure === 1) {
            recommendedScheme = scheme;
          }
        });
        if (recommendedScheme) {
          this.#RECOMMENDED_KEY = this.#getSchemeTypeTN(
            recommendedScheme.schemeType
          );
          this.#recommendedPremiumData = this.#computePremium(
            this.#RECOMMENDED_KEY,
            Object.entries(recommendedScheme?.packages || {})
              .filter(([_, pkg]) => pkg?.isMandatory && pkg?.value !== 0)
              .map(([key]) => key)
          );
        }
      }
    }
  }

  /**
   *
   *  @param {Object[]} schemes premium schem list/array from backend response
   *  @param {boolean} isCust to be true, if is to initialized for customer
   */
  static init(schemes, isCust = false) {
    if (!PremiumCompute.#instance) {
      PremiumCompute.#instance = new PremiumCompute(schemes, isCust);
    }
  }

  /**
   *
   *  @returns Object: (refer class docs)
   */
  static getInitMapping() {
    const allSchemePremiumData = PremiumCompute.describeAll();
    return PremiumCompute.#instance.#prepareInitMapping(allSchemePremiumData);
  }

  /**
   *
   *  @param {string} schemeTypeName scheme name with or without transformation (remove space and to lower case)
   *  @param {string[]} selectedPackageNameList string list/array with package keys in package object.
   *  @returns Object: COMPUTE_PREMIUM_DATA_OBJECT (refer class docs)
   */
  static compute(
    schemeTypeName,
    selectedPackageNameList,
    selectedAddonCovers = null,
    isMedicareSelectPolicy = false
  ) {
    if (!schemeTypeName || typeof schemeTypeName !== "string") {
      throw new Error("Invalid scheme type");
    }
    if (!Array.isArray(selectedPackageNameList)) {
      throw new Error("Invalid package selection");
    }
    const schemeTypeNameTn =
      PremiumCompute.#instance.#getSchemeTypeTN(schemeTypeName);
    return PremiumCompute.#instance.#computePremium(
      schemeTypeNameTn,
      selectedPackageNameList,
      selectedAddonCovers,
      isMedicareSelectPolicy
    );
  }

  /**
   *  Can be called to get As Is scheme premium data
   *  @returns Object: COMPUTE_PREMIUM_DATA_OBJECT (refer class docs)
   */
  static getAsIs() {
    return PremiumCompute.#instance.#asisPremiumData;
  }

  /**
   *  Can be called to get Recommended scheme premium data
   *  @returns Object: COMPUTE_PREMIUM_DATA_OBJECT (refer class docs)
   */
  static getRecommended() {
    return PremiumCompute.#instance.#recommendedPremiumData;
  }

  /**
   *  Can be called to get Exception As-Is scheme premium data
   *  @returns Object: COMPUTE_PREMIUM_DATA_OBJECT
   */
  static getExceptionAsIs() {
    return PremiumCompute.#instance.#exceptionAsisPremiumData;
  }

  /**
   *  Can be called to get all scheme premium data
   *  @returns Object: { [key: string]: COMPUTE_PREMIUM_DATA_OBJECT } (refer class docs)
   */
  static describeAll(allPackageSelected = false) {
    const describedObject = {};
    Object.keys(PremiumCompute.#instance.#schemeTypeMap).forEach(
      (schemeTypeNameTn) => {
        const scheme =
          PremiumCompute.#instance.#schemeTypeMap[schemeTypeNameTn];
        describedObject[schemeTypeNameTn] =
          PremiumCompute.#instance.#computePremium(
            schemeTypeNameTn,
            allPackageSelected ? Object.keys(scheme?.packages) : []
          );
      }
    );
    return describedObject;
  }

  static mapCalculatedPremium(premiumDtls, meta) {
    if (!PremiumCompute.#instance) {
      PremiumCompute.#instance = new PremiumCompute([], false);
    }
    return PremiumCompute.#instance.#mapCalculatedPremium(premiumDtls, meta);
  }

  /**
   * Get all schemes where isMigrationPolicy === true
   * Returns mapped COMPUTE_PREMIUM_DATA_OBJECTs (same as preparePremiumData output)
   * @returns {Array<Object>} Array of computed premium data for migration schemes
   */
  static getMigrationMappedSchemes() {
    if (!PremiumCompute.#instance) return [];

    const inst = PremiumCompute.#instance;
    const migrationSchemes = Object.entries(inst.#schemeTypeMap).filter(
      ([, scheme]) => scheme?.isMigrationPolicy === true
    );

    return migrationSchemes.map(([tn, scheme]) => {
      const mandatoryPackages = Object.entries(scheme?.packages || {})
        .filter(([, pkg]) => pkg?.isMandatory && pkg?.value !== 0)
        .map(([key]) => key);

      // compute premium using the same internal method as others
      return inst.#computePremium(tn, mandatoryPackages);
    });
  }

  #prepareInitMapping(allSchemePremiumData) {
    const lowerTenureSelectionDisable = true;
    const tenureSet = new Set();
    const siSet = new Set();
    const tenureSiSchemeMap = {};

    if (typeof allSchemePremiumData === "object") {
      Object.keys(allSchemePremiumData).forEach((schemeTypeNameTn) => {
        if (schemeTypeNameTn === "exceptionas-is") return;
        const schemePremiumData = allSchemePremiumData[schemeTypeNameTn];
        const schemeTenureSI = this.#schemeTypeInfoMap[schemeTypeNameTn];
        const schemeData = this.#schemeTypeMap[schemeTypeNameTn];

        if (
          schemeData?.isMigrationPolicy === true ||
          schemeData?.isMigration === true
        ) {
          return;
        }

        const sSI = this.#getShortSI(schemeTenureSI.sumInsured);
        siSet.add(sSI);
        tenureSet.add(schemeTenureSI.tenure);

        const packages = schemeData?.packages;
        let isAllPackageMandatory = false;

        if (typeof packages === "object") {
          isAllPackageMandatory = Object.values(packages).every(
            (eachPackage) => {
              return "isMandatory" in eachPackage
                ? eachPackage?.isMandatory
                : true;
            }
          );
        }

        tenureSiSchemeMap[`_T-${schemeTenureSI.tenure}_SI-${sSI}`] = {
          schemeTypeNameTn: schemeTypeNameTn,
          schemeTypeName: schemePremiumData?.schemeTypeName || schemeTypeNameTn,
          packagesSelected: Object.keys(packages || {}),
          packages: packages || {},
          isAllPackageMandatory: isAllPackageMandatory,
        };
      });
    }

    if (
      this.#isCust &&
      !!this.#recommendedPremiumData &&
      ((siSet.size === 2 && tenureSet.size === 1) ||
        (siSet.size === 1 && tenureSet.size === 2))
    ) {
      const asisTenureSI = this.#asisPremiumData?.tenureSI;
      const recommendedTenureSI = this.#recommendedPremiumData?.tenureSI;
      const recommtenureSiSchemeData =
        tenureSiSchemeMap[
          `${recommendedTenureSI.tenureId}${recommendedTenureSI.sumInsuredId}`
        ];
      const asistenureSiSchemeData =
        tenureSiSchemeMap[
          `${asisTenureSI.tenureId}${asisTenureSI.sumInsuredId}`
        ];
      if (
        recommtenureSiSchemeData?.isAllPackageMandatory &&
        asistenureSiSchemeData?.isAllPackageMandatory
      ) {
        siSet.clear();
        tenureSet.clear();
      }
    } else if (this.#isCust && siSet.size === 1 && tenureSet.size === 1) {
      const asisTenureSI = this.#asisPremiumData?.tenureSI;
      const tenureSiSchemeData =
        tenureSiSchemeMap[
          `${asisTenureSI.tenureId}${asisTenureSI.sumInsuredId}`
        ];
      if (tenureSiSchemeData?.isAllPackageMandatory) {
        siSet.clear();
        tenureSet.clear();
      }
    }

    const asisTenureSI = this.#schemeTypeInfoMap[this.#ASIS_KEY];

    const tenureList = Array.from(tenureSet)
      .sort()
      .map((tenure) => {
        let disabled = false;
        if (lowerTenureSelectionDisable && tenure < asisTenureSI.tenure) {
          disabled = true;
        }
        return {
          id: `_T-${tenure}`,
          value: tenure,
          tenure: tenure,
          disabled: disabled,
        };
      });

    const siList = Array.from(siSet)
      .sort()
      .map((si) => {
        let disabled = false;
        return {
          id: `_SI-${si}`,
          value: si,
          si: si * this.#SI_MULTIPLE_IN,
          disabled: disabled,
        };
      });

    return {
      tenureList: tenureList,
      siList: siList,
      tenureSiSchemeMap: tenureSiSchemeMap,
    };
  }

  #getSI(scheme) {
    if (
      scheme &&
      scheme?.memberDetails &&
      Array.isArray(scheme?.memberDetails)
    ) {
      let maxMemberAge = -1;
      let maxMemberAgeSI = 0;
      let selfSI = -1;
      scheme?.memberDetails.forEach((memberDetail) => {
        if (memberDetail?.relationship?.toLowerCase() === "self") {
          selfSI = memberDetail?.sumInsured;
        }
        if (maxMemberAge < (memberDetail?.age || 0)) {
          maxMemberAge = memberDetail?.age || 0;
          maxMemberAgeSI = memberDetail?.sumInsured;
        }
      });
      if (selfSI <= 0) {
        return maxMemberAgeSI || 0;
      }
      return selfSI;
    }
    return 0;
  }

  #computePremium(
    schemeTypeNameTn,
    selectedPackageNameList,
    selectedAddonCovers = null,
    isMedicareSelectPolicy = false
  ) {
    let schemeData = this.#schemeTypeMap[schemeTypeNameTn];

    const packagePremium = this.#computePackagePremium(
      selectedPackageNameList,
      schemeData?.packages,
      this.#isCust
    );

    const addonCoverPremiumDelta = isMedicareSelectPolicy
      ? this.#computeAddonCoverPremiumDelta(
          selectedAddonCovers,
          schemeData?.addonCovers
        )
      : {
          premiumInDecimal: 0,
          gstInDecimal: 0,
          totalWithGSTInDecimal: 0,
          totalWithGST: 0,
          breakup: {
            premiumInDecimal: 0,
            premium: 0,
            gstInDecimal: 0,
            gst: 0,
          },
        };

    if (schemeData) {
      return this.#preparePremiumData(
        schemeTypeNameTn,
        schemeData,
        packagePremium,
        addonCoverPremiumDelta,
        isMedicareSelectPolicy
      );
    }

    if (!schemeData && this.allSchemes && this.allSchemes[schemeTypeNameTn]) {
      schemeData = this.allSchemes[schemeTypeNameTn];
    }

    return null;
  }
  #getSchemeTypeTN(schemeTypeName) {
    if (typeof schemeTypeName === "string") {
      return schemeTypeName.replace(/\s/g, "").toLowerCase();
    }
    return schemeTypeName;
  }

  #collectRiders(ridersSet, ridersList) {
    if (Array.isArray(ridersList) && ridersSet instanceof Set) {
      ridersList.forEach((rider) => ridersSet.add(rider));
    }
  }

  #computePackagePremium(selectedPackageNameList, packageData, isCust) {
    let count = 0;
    let premium = 0;
    let gst = 0;
    const ridersSet = new Set();
    const alwaysIncludeMandatory = false;
    if (!!packageData && typeof packageData === "object") {
      if (alwaysIncludeMandatory) {
        const selectedPackageNameSet = new Set(selectedPackageNameList);
        Object.keys(packageData).forEach((key) => {
          const packageDataItem = packageData[key];
          if (packageDataItem) {
            if (selectedPackageNameSet.has(key)) {
              premium += packageDataItem?.value || 0;
              gst += packageDataItem?.gstval || 0;
              count++;
              this.#collectRiders(ridersSet, packageDataItem?.riders);
            } else {
              if (packageDataItem?.isMandatory && !!isCust) {
                premium += packageDataItem?.value || 0;
                gst += packageDataItem?.gstval || 0;
                count++;
                this.#collectRiders(ridersSet, packageDataItem?.riders);
              }
            }
          }
        });
      } else {
        selectedPackageNameList.forEach((selectedPackageName) => {
          const packageDataItem = packageData[selectedPackageName];

          if (
            packageDataItem &&
            (packageDataItem.isMandatory ||
              selectedPackageNameList.includes(selectedPackageName))
          ) {
            premium += packageDataItem?.value || 0;
            gst += packageDataItem?.gstval || 0;
            count++;
            this.#collectRiders(ridersSet, packageDataItem?.riders);
          }
        });
      }
    }
    const totalPackagePremium = Number(premium || 0) + Number(gst || 0);
    return {
      riders: Array.from(ridersSet).sort(),
      count: count,
      selectedPackagesList: selectedPackageNameList,
      premiumWithGSTInDecimal: this.#roundOff(totalPackagePremium),
      premiumWithGST: this.#roundOff(totalPackagePremium, 0),
      premiumBreakup: {
        premiumInDecimal: this.#roundOff(premium),
        premium: this.#roundOff(premium, 0),
        gstInDecimal: this.#roundOff(gst),
        gst: this.#roundOff(gst, 0),
      },
    };
  }

  /**
   * Computes addon cover premium delta (add/deduct) vs scheme's initially included addon covers.
   *
   * @param {Object|null} selectedAddonCovers - UI selected addon covers map keyed by cover code (e.g., C014)
   * @param {Object|null} schemeAddonCovers - schemeData.addonCovers
   * @returns {{
   *   premiumInDecimal:number, gstInDecimal:number, totalWithGSTInDecimal:number, totalWithGST:number,
   *   breakup:{premiumInDecimal:number,premium:number,gstInDecimal:number,gst:number}
   * }}
   */
  #computeAddonCoverPremiumDelta(selectedAddonCovers, schemeAddonCovers) {
    if (!selectedAddonCovers || typeof selectedAddonCovers !== "object") {
      return {
        premiumInDecimal: 0,
        gstInDecimal: 0,
        totalWithGSTInDecimal: 0,
        totalWithGST: 0,
        breakup: {
          premiumInDecimal: 0,
          premium: 0,
          gstInDecimal: 0,
          gst: 0,
        },
      };
    }

    const IGNORE_ADDON_COVERS = ["C007", "C005"];

    const initial = { prem: 0, gst: 0 };
    const current = { prem: 0, gst: 0 };

    const raw = schemeAddonCovers || {};

    Object.entries(raw).forEach(([coverKey, coverDef]) => {
      if (!coverDef) return;
      if (IGNORE_ADDON_COVERS.includes(coverKey)) return;

      const initCost = this.#sumCoverCostFromScheme(coverDef);
      initial.prem += initCost.prem;
      initial.gst += initCost.gst;

      const selectedCover = selectedAddonCovers?.[coverKey];
      const currCost = this.#sumCoverCostFromSelection(selectedCover, coverDef);
      current.prem += currCost.prem;
      current.gst += currCost.gst;
    });

    const deltaPrem = Number(current.prem || 0) - Number(initial.prem || 0);
    const deltaGst = Number(current.gst || 0) - Number(initial.gst || 0);
    const deltaTotal = Number(deltaPrem || 0) + Number(deltaGst || 0);

    return {
      premiumInDecimal: this.#roundOff(deltaPrem),
      gstInDecimal: this.#roundOff(deltaGst),
      totalWithGSTInDecimal: this.#roundOff(deltaTotal),
      totalWithGST: this.#roundOff(deltaTotal, 0),
      breakup: {
        premiumInDecimal: this.#roundOff(deltaPrem),
        premium: this.#roundOff(deltaPrem, 0),
        gstInDecimal: this.#roundOff(deltaGst),
        gst: this.#roundOff(deltaGst, 0),
      },
    };
  }

  #sumCoverCostFromScheme(coverDef) {
    let prem = 0;
    let gst = 0;

    const rawValue = coverDef?.value;

    if (Array.isArray(rawValue)) {
      rawValue.forEach((opt) => {
        if (!opt || opt.opted !== true) return;
        const p = Number(opt?.value ?? 0);
        const g = Number(opt?.gstval ?? opt?.gstVal ?? opt?.gstVal ?? 0);

        prem += p;
        gst += g;
      });
    } else {
      if (coverDef?.opted !== true) return { prem: 0, gst: 0 };
      const p = Number(coverDef?.value ?? 0);
      const g = Number(coverDef?.gstval ?? coverDef?.gstVal ?? 0);

      prem += p;
      gst += g;
    }

    return { prem, gst };
  }

  #sumCoverCostFromSelection(selectedCover, schemeDef) {
    let prem = 0;
    let gst = 0;

    if (!selectedCover) return { prem: 0, gst: 0 };

    const schemeValue = schemeDef?.value;

    if (Array.isArray(schemeValue)) {
      const selectedList = Array.isArray(selectedCover?.value)
        ? selectedCover.value
        : Array.isArray(selectedCover)
        ? selectedCover
        : null;

      if (!selectedList) {
        const selectedName = selectedCover?.selected;
        if (selectedName) {
          const opt = schemeValue.find((x) => x?.name === selectedName);
          if (opt) {
            prem += Number(opt?.value ?? 0);
            gst += Number(opt?.gstval ?? opt?.gstVal ?? 0);
          }
        }
        return { prem, gst };
      }

      selectedList.forEach((sel) => {
        if (!sel || sel.opted !== true) return;

        const opt = schemeValue.find(
          (x) => String(x?.name) === String(sel?.name)
        );
        if (!opt) return;

        const p = Number(opt?.value ?? 0);
        const g = Number(opt?.gstval ?? opt?.gstVal ?? 0);

        prem += p;
        gst += g;
      });

      return { prem, gst };
    }

    const isOpted = selectedCover?.opted === true;
    if (!isOpted) return { prem: 0, gst: 0 };

    prem += Number(schemeDef?.value ?? selectedCover?.value ?? 0);
    gst += Number(
      schemeDef?.gstval ??
        schemeDef?.gstVal ??
        selectedCover?.gstval ??
        selectedCover?.gstVal ??
        0
    );

    return { prem, gst };
  }

  #preparePremiumData(
    schemeTypeNameTn,
    schemeData,
    packageData,
    addonCoverPremiumDelta = null,
    isMedicareSelectPolicy = false
  ) {
    let gst = schemeData?.gstWithoutMandatoryPackage?.gst;
    let gstVal = schemeData?.gstWithoutMandatoryPackage?.gstval;

    let premiumInDecimal =
      Number(schemeData?.premiumWithoutMandatoryPackagesWithoutGst || 0) +
      Number(gstVal || 0);

    let totalPremiumInDecimal =
      Number(premiumInDecimal || 0) +
      Number(packageData?.premiumWithGSTInDecimal || 0) +
      (isMedicareSelectPolicy
        ? Number(addonCoverPremiumDelta?.totalWithGSTInDecimal || 0)
        : 0);

    let totalPremium = this.#roundOff(totalPremiumInDecimal, 0);
    let totalPremiumInDecimalRounded = this.#roundOff(totalPremiumInDecimal);

    if (
      schemeTypeNameTn === this.#EXCEPTION_KEY ||
      schemeData.isMigrationPolicy
    ) {
      totalPremium = this.#roundOff(schemeData?.premium || 0, 0);
      totalPremiumInDecimalRounded = this.#roundOff(
        schemeData?.premiumInDecimal || 0
      );
    }

    const premiumData = {
      schemeTypeNameTn: schemeTypeNameTn,
      schemeTypeName: schemeData?.schemeType,
      basePremium: schemeData?.basePremium || 0,
      totalDiscount: schemeData?.totalDiscount || 0,
      totalLoading: schemeData?.totalLoading || 0,
      premiumWithoutGST:
        schemeData?.premiumWithoutMandatoryPackagesWithoutGst || 0,
      premiumGST: gst || 0,
      premiumGSTVal: gstVal || 0,
      premiumInDecimal: this.#roundOff(premiumInDecimal) || 0,
      premium: this.#roundOff(premiumInDecimal, 0),
      packageCount: packageData?.count || 0,
      packageList: (packageData?.selectedPackagesList || []).map(
        (pkgKey) => schemeData?.packages?.[pkgKey]?.name || pkgKey
      ),
      riders: packageData?.riders || [],
      packagePremiumWithGST: packageData?.premiumWithGST || 0,
      packagePremiumBreakup: packageData?.premiumBreakup || {},
      addonCoverPremiumDeltaWithGST: addonCoverPremiumDelta?.totalWithGST || 0,
      addonCoverPremiumDeltaBreakup: addonCoverPremiumDelta?.breakup || {},
      totalPremium: totalPremium,
      totalPremiumInDecimal: totalPremiumInDecimalRounded,
      isMigrationPolicy: schemeData.isMigrationPolicy,
    };

    const IGNORE_ADDON_COVERS = ["C007", "C005"];
    const rawAddonCovers = schemeData?.addonCovers || {};
    const addonCovers = {};

    Object.entries(rawAddonCovers).forEach(([coverKey, cover]) => {
      if (!cover) return;

      if (IGNORE_ADDON_COVERS.includes(coverKey)) return;

      const rawValue = cover.value;

      if (Array.isArray(rawValue)) {
        const optedValues = rawValue.filter((v) => v);

        if (!optedValues.length) return;

        addonCovers[coverKey] = {
          name: cover.name,
          viewPermissions: cover.viewPermissions || [],
          eligibleProductCode: cover.eligibleProductCode || [],
          opted: cover.opted,
          value: optedValues,
          percentage: cover.percentage ?? 0,
        };
      } else {
        // if (!cover.opted) return;

        addonCovers[coverKey] = {
          name: cover.name,
          viewPermissions: cover.viewPermissions || [],
          eligibleProductCode: cover.eligibleProductCode || [],
          opted: cover.opted,
          value: rawValue,
          percentage: cover.percentage ?? 0,
        };
      }
    });

    if (Object.keys(addonCovers).length > 0) {
      premiumData.addonCovers = addonCovers;
    }

    const selectedTenureSI = this.#schemeTypeInfoMap[schemeTypeNameTn];
    if (selectedTenureSI) {
      const sSI = this.#getShortSI(selectedTenureSI?.sumInsured || 0);

      premiumData["tenureSI"] = {
        tenureId: `_T-${selectedTenureSI?.tenure || 0}`,
        tenure: selectedTenureSI?.tenure || 0,
        sumInsuredId: `_SI-${sSI || 0}`,
        sumInsured: selectedTenureSI?.sumInsured || 0,
        shortSumInsured: sSI || 0,
      };

      if (schemeTypeNameTn !== this.#ASIS_KEY) {
        const benefits = this.#estimateBenifit(premiumData, selectedTenureSI);
        if (benefits) {
          premiumData["benefits"] = benefits;
        }
      }
    }

    return premiumData;
  }

  #estimateBenifit(selectedPremiumData, selectedTenureSI) {
    if (this.#asisPremiumData) {
      const asisPremiumData = this.#asisPremiumData;
      const asisTenureSI = this.#schemeTypeInfoMap[this.#ASIS_KEY];

      let percentage = 0;
      let additionalSI = 0;

      let asisPremiumPerLacPerYear =
        (((asisPremiumData?.totalPremium || 0) /
          (asisTenureSI?.sumInsured || 1)) *
          100000) /
        (asisTenureSI?.tenure || 1);
      let selectedPremiumPerLacPerYear =
        (((selectedPremiumData?.totalPremium || 0) /
          (selectedTenureSI?.sumInsured || 1)) *
          100000) /
        (selectedTenureSI?.tenure || 1);

      if (
        asisTenureSI?.sumInsured === selectedTenureSI?.sumInsured &&
        asisTenureSI?.tenure !== selectedTenureSI?.tenure
      ) {
        additionalSI = 0;
        if (asisPremiumPerLacPerYear > selectedPremiumPerLacPerYear) {
          percentage =
            ((asisPremiumPerLacPerYear - selectedPremiumPerLacPerYear) /
              (asisPremiumPerLacPerYear || 1)) *
            100;
        } else {
          percentage = 0;
        }
      } else if (
        asisTenureSI?.sumInsured !== selectedTenureSI?.sumInsured &&
        asisTenureSI?.tenure === selectedTenureSI?.tenure
      ) {
        additionalSI =
          (selectedTenureSI?.sumInsured || 0) - (asisTenureSI?.sumInsured || 0);
        percentage = 0;
      } else {
        additionalSI =
          (selectedTenureSI?.sumInsured || 0) - (asisTenureSI?.sumInsured || 0);
        if (asisPremiumPerLacPerYear > selectedPremiumPerLacPerYear) {
          percentage =
            ((asisPremiumPerLacPerYear - selectedPremiumPerLacPerYear) /
              (asisPremiumPerLacPerYear || 1)) *
            100;
        } else {
          percentage = 0;
        }
      }

      return {
        savePercentage: this.#roundOff(percentage, 0),
        additionalSI: additionalSI,
      };
    }
    return null;
  }

  #roundOff(number, decimalDigits = 2) {
    if (typeof number === "number") {
      return Number(number.toFixed(decimalDigits));
    }
    return number;
  }

  #getShortSI = (si) => {
    if (typeof si === "number") {
      const shortSI = si / this.#SI_MULTIPLE_IN;
      return shortSI < 1 ? 0 : this.#roundOff(shortSI, 1);
    }
    return si;
  };

  #mapCalculatedPremium(premiumDtls, meta) {
    const schemeTypeName = meta?.schemeTypeName;
    const isMedicareSelectPolicy = meta.isMedicareSelectPolicy;
    const schemeTypeNameTn = this.#getSchemeTypeTN(schemeTypeName);

    const pkgObj = premiumDtls?.packages ?? {};
    const selectedPkgKeys = Object.keys(pkgObj).filter(
      (k) => (pkgObj[k]?.isMandatory || pkgObj[k]?.isOpted) && pkgObj[k]?.value !== 0
    );

    const packageRidersObj = premiumDtls?.packageRiders ?? {};
    const riders = Object.keys(packageRidersObj).sort();

    const isAllPackageMandatory = Object.values(pkgObj || {}).every(
      (eachPackage) => {
        return "isMandatory" in (eachPackage || {})
          ? !!eachPackage?.isMandatory
          : true;
      }
    );

    const packagesBreakup = selectedPkgKeys.map((k) => {
      const p = pkgObj[k] || {};
      const value = Number(p?.value || 0);
      const gstval = Number(p?.gstval || 0);

      return {
        key: k,
        name: p?.name || k,
        isMandatory: !!p?.isMandatory,
        isOpted: !!p?.isOpted,
        premiumInDecimal: this.#roundOff(value),
        premium: this.#roundOff(value, 0),
        gstInDecimal: this.#roundOff(gstval),
        gst: this.#roundOff(gstval, 0),
        totalInDecimal: this.#roundOff(value + gstval),
        total: this.#roundOff(value + gstval, 0),
      };
    });

    let pkgPrem = 0;
    let pkgGst = 0;

    selectedPkgKeys.forEach((k) => {
      pkgPrem += Number(pkgObj[k]?.value || 0);
      pkgGst += Number(pkgObj[k]?.gstval || 0);
    });

    const tenure = Number(premiumDtls?.tenure);
    const si =
      Number(
        premiumDtls?.memberDetails?.find(
          (m) => m?.relationship?.toLowerCase() === "self"
        )?.sumInsured
      ) ||
      Number(premiumDtls?.memberDetails?.[0]?.sumInsured) ||
      0;

    const shortSI = this.#getShortSI(si);

    const pwog = Number(
      premiumDtls?.premiumWithoutMandatoryPackagesWithoutGst ??
        premiumDtls?.premiumWithoutGST ??
        0
    );

    const gstVal = Number(
      premiumDtls?.gstWithoutMandatoryPackage?.gstval ??
        premiumDtls?.gst?.gstval ??
        0
    );

    const gst = Number(
      premiumDtls?.gstWithoutMandatoryPackage?.gst ?? premiumDtls?.gst?.gst ?? 0
    );

    const premiumInDecimal = Number(pwog || 0) + Number(gstVal || 0);

    const IGNORE_ADDON_COVERS = ["C007", "C005"];
    const rawAddonCovers = premiumDtls?.addonCovers ?? {};
    const addonCovers = {};
    const ignoredAddonCovers = {};

    let addonPrem = 0;
    let addonGst = 0;

    Object.entries(rawAddonCovers).forEach(([coverKey, cover]) => {
      if (!cover) return;

      const isIgnored = IGNORE_ADDON_COVERS.includes(coverKey);
      const rawValue = cover?.value;

      if (isIgnored) {
        ignoredAddonCovers[coverKey] = {
          name: cover?.name,
          viewPermissions: cover?.viewPermissions || [],
          eligibleProductCode: cover?.eligibleProductCode || [],
          opted: cover?.opted,
          value: rawValue,
          percentage: cover?.percentage ?? 0,
        };
        return;
      }

      if (Array.isArray(rawValue)) {
        const optedValues = rawValue.filter((v) => v);

        if (!optedValues.length) return;

        addonCovers[coverKey] = {
          name: cover?.name,
          viewPermissions: cover?.viewPermissions || [],
          eligibleProductCode: cover?.eligibleProductCode || [],
          opted: cover?.opted,
          value: optedValues,
          percentage: cover?.percentage ?? 0,
        };
      } else {
        addonCovers[coverKey] = {
          name: cover?.name,
          viewPermissions: cover?.viewPermissions || [],
          eligibleProductCode: cover?.eligibleProductCode || [],
          opted: cover?.opted,
          value: rawValue,
          percentage: cover?.percentage ?? 0,
        };
      }
    });

    const addonCoverPremiumDelta = {
      premiumInDecimal: this.#roundOff(addonPrem),
      gstInDecimal: this.#roundOff(addonGst),
      totalWithGSTInDecimal: this.#roundOff(addonPrem + addonGst),
      totalWithGST: this.#roundOff(addonPrem + addonGst, 0),
      breakup: {
        premiumInDecimal: this.#roundOff(addonPrem),
        premium: this.#roundOff(addonPrem, 0),
        gstInDecimal: this.#roundOff(addonGst),
        gst: this.#roundOff(addonGst, 0),
      },
    };

    const totalPremiumInDecimal =
      Number(premiumInDecimal || 0) +
      Number(pkgPrem || 0) +
      Number(pkgGst || 0) +
      (isMedicareSelectPolicy
        ? Number(addonCoverPremiumDelta?.totalWithGSTInDecimal || 0)
        : 0);

    const totalPremium = this.#roundOff(totalPremiumInDecimal, 0);

    const obj = {
      schemeTypeNameTn,
      schemeTypeName,

      basePremium: premiumDtls?.basePremium ?? 0,
      totalDiscount: premiumDtls?.totalDiscount ?? 0,
      totalLoading: premiumDtls?.totalLoading ?? 0,

      premiumWithoutGST: pwog,
      premiumGST: gst,
      premiumGSTVal: gstVal,
      premiumWithoutMandatoryPackagesWithoutGst: pwog,
      gstWithoutMandatoryPackage: {
        gst,
        gstval: gstVal,
      },

      premiumInDecimal: this.#roundOff(premiumInDecimal),
      premium: this.#roundOff(premiumInDecimal, 0),

      packageCount: selectedPkgKeys.length,
      packageList: selectedPkgKeys.map((k) => pkgObj[k]?.name || k),
      riders,

      packagePremiumWithGST: this.#roundOff(pkgPrem + pkgGst, 0),
      packagePremiumBreakup: {
        premiumInDecimal: this.#roundOff(pkgPrem),
        premium: this.#roundOff(pkgPrem, 0),
        gstInDecimal: this.#roundOff(pkgGst),
        gst: this.#roundOff(pkgGst, 0),
      },

      addonCoverPremiumDeltaWithGST: addonCoverPremiumDelta?.totalWithGST || 0,
      addonCoverPremiumDeltaBreakup: addonCoverPremiumDelta?.breakup || {},

      totalPremium,
      totalPremiumInDecimal: this.#roundOff(totalPremiumInDecimal),

      tenureSI: {
        tenureId: `_T-${tenure}`,
        tenure,
        sumInsuredId: `_SI-${shortSI}`,
        sumInsured: si,
        shortSumInsured: shortSI,
      },

      packages: pkgObj,
      packagesSelected: selectedPkgKeys,
      isAllPackageMandatory,
      packagesBreakup,
      packageRiders: packageRidersObj,
      isMigrationPolicy: premiumDtls?.isMigrationPolicy || false,
    };

    if (Object.keys(addonCovers).length > 0) {
      obj.addonCovers = addonCovers;
    }

    if (Object.keys(ignoredAddonCovers).length > 0) {
      obj.ignoredAddonCovers = ignoredAddonCovers;
    }

    const benefits = this.#estimateBenifit(obj, { tenure, sumInsured: si });
    if (benefits) obj.benefits = benefits;

    if (premiumDtls?.schemeType) {
      PremiumCompute.registerExternalScheme(
        premiumDtls.schemeType,
        premiumDtls
      );
    }

    return obj;
  }

  static registerExternalScheme(schemeTypeName, schemeObject) {
    if (!PremiumCompute.#instance) return;

    if (!schemeTypeName || typeof schemeTypeName !== "string") {
      console.warn(
        "PremiumCompute.registerExternalScheme called with invalid schemeTypeName",
        schemeTypeName
      );
      return;
    }

    const tn = schemeTypeName.replace(/\s/g, "").toLowerCase();

    PremiumCompute.#instance.#schemeTypeMap[tn] = {
      ...schemeObject,
      schemeType: schemeObject.schemeType || schemeTypeName,
      packages: schemeObject?.packages || {},
    };

    PremiumCompute.#instance.#schemeTypeInfoMap[tn] = {
      sumInsured: schemeObject?.tenureSI?.sumInsured || 0,
      tenure: schemeObject?.tenureSI?.tenure || 0,
    };
  }
}

export default PremiumCompute;
